import { zodResolver } from "@hookform/resolvers/zod";
import { Button, Card, Col, Form, Row } from "antd";
import dayjs from "dayjs";
import * as React from "react";
import { DefaultValues, useForm } from "react-hook-form";
import * as z from "zod";
import { schemaFormAudit } from "../../../features/forms/schemas/schemaFormAudit";
import { Audit } from "../../../types/audits";
import { AuditType } from "../../../types/auditTypes";
import { Site } from "../../../types/sites";
import { Role, UserForList } from "../../../types/user";
import { isDev } from "../../../utils/dev";
import StatusBadges from "../../tables/StatusBadges";
import Br from "../../ui/Br";
import spacing from "../../ui/spacingConfig";
import FieldDate from "../fields/FieldDate";
import FieldInput from "../fields/FieldInput";
import FieldMultiSelect from "../fields/FieldMultiSelect";
import FieldSelect from "../fields/FieldSelect";
import FieldTextArea from "../fields/FieldTextArea";
import FieldUpload from "../fields/FieldUpload/FieldUpload";
import useSubmit from "./useSubmit";

export type FormAuditSchema = z.infer<typeof schemaFormAudit>;

const DEFAULT_SHARED_ROLE_NAME = "sdlp-superadmin";

type Props = {
  isEditMode?: boolean;
  audit?: Audit;
  sites?: Site[];
  auditTypes?: AuditType[];
  users?: UserForList[];
  roles?: Role[];
};

const getDefaultValue = (
  edit: boolean,
  audit?: Audit,
): DefaultValues<FormAuditSchema> => {
  if (!edit || !audit) {
    return {
      title: "",
      description: "",
      site: "",
      audit_type: "",
      dateAudit: undefined,
      accessGivenTo: [],
      accessRoleGivenTo: [],
    };
  }
  return {
    title: audit.title,
    description: audit.description || "",
    site: audit.site ? String(audit.site.id) : "",
    audit_type: audit.audit_type ? String(audit.audit_type.id) : "",
    dateAudit: audit.dateAudit ? dayjs(audit.dateAudit) : undefined,
    accessGivenTo: audit.accessGivenTo?.map((user) => String(user.id)) || [],
    accessRoleGivenTo:
      audit.accessRoleGivenTo?.map((role) => String(role.id)) || [],
  };
};

const FormAudit: React.FC<Props> = ({
  isEditMode,
  audit,
  sites,
  auditTypes,
  users,
  roles,
}) => {
  const { onSubmit, isLoading } = useSubmit(isEditMode, audit);

  const {
    control,
    handleSubmit,
    watch,
    setValue,
    getValues,
    formState: { errors },
  } = useForm<FormAuditSchema>({
    defaultValues: getDefaultValue(!!isEditMode, audit),
    resolver: zodResolver(schemaFormAudit),
  });

  const appliedDefaultRoleRef = React.useRef(false);

  React.useEffect(() => {
    // useEffect pour recuperer le role admin par defaut
    if (appliedDefaultRoleRef.current) return;
    const defaultRole = roles?.find(
      (role) => role.name === DEFAULT_SHARED_ROLE_NAME,
    );
    if (!defaultRole) return;
    appliedDefaultRoleRef.current = true;
    const defaultRoleId = String(defaultRole.id);
    const currentRoleIds = getValues("accessRoleGivenTo") || [];
    if (!currentRoleIds.includes(defaultRoleId)) {
      setValue("accessRoleGivenTo", [...currentRoleIds, defaultRoleId]);
    }
  }, [roles, getValues, setValue]);

  if (isDev) {
    const formValues = watch();
    console.log("formValues", formValues);
    console.log("errors", errors);
  }

  return (
    <Form
      labelCol={{ span: spacing.tiny }}
      wrapperCol={{ span: spacing.sPlus }}
      layout="horizontal"
      onFinish={handleSubmit(onSubmit)}
      labelAlign="left"
      colon={false}
    >
      {isEditMode && audit && (
        <>
          <Row gutter={spacing.m}>
            <Col>
              <StatusBadges value={audit.status} type="audits" />
            </Col>
          </Row>
          <Br height={spacing.m} />
        </>
      )}
      <Row gutter={spacing.m}>
        <Col span={14}>
          <Card title="Description" bordered={false}>
            <FieldInput<FormAuditSchema>
              errorMessage={errors?.title?.message}
              control={control}
              name="title"
              label="Titre"
              placeholder="Titre..."
            />
            <FieldTextArea<FormAuditSchema>
              errorMessage={errors?.description?.message}
              control={control}
              name="description"
              label="Description"
              placeholder="Description..."
            />
          </Card>
        </Col>
        <Col span={10}>
          <Card title="DÃ©tails" bordered={false}>
            <FieldSelect<FormAuditSchema>
              errorMessage={errors?.site?.message}
              control={control}
              name="site"
              label="Site"
              placeholder="Choisir un site"
              options={
                sites?.map((site) => ({
                  value: String(site.id),
                  label: site.name,
                })) || []
              }
            />
            <FieldSelect<FormAuditSchema>
              errorMessage={errors?.audit_type?.message}
              control={control}
              name="audit_type"
              label="Type d'audit"
              placeholder="Choisir un type d'audit"
              options={
                auditTypes?.map((auditType) => ({
                  value: String(auditType.id),
                  label: auditType.name,
                })) || []
              }
            />
            <FieldDate<FormAuditSchema>
              errorMessage={errors?.dateAudit?.message}
              control={control}
              name="dateAudit"
              label="Date de l'audit"
            />
          </Card>
        </Col>
      </Row>
      <Br height={spacing.xl} />
      <Row gutter={spacing.m}>
        <Col span={14}>
          <Card title="AccÃ¨s" bordered={false}>
            <FieldMultiSelect<FormAuditSchema>
              errorMessage={errors?.accessRoleGivenTo?.message}
              control={control}
              name="accessRoleGivenTo"
              label="Groupe de partage"
              placeholder="Donner accÃ¨s Ã  des rÃ´les"
              options={
                roles?.map((role) => ({
                  value: String(role.id),
                  label: role.displayName,
                })) || []
              }
            />
            <FieldMultiSelect<FormAuditSchema>
              errorMessage={errors?.accessGivenTo?.message}
              control={control}
              name="accessGivenTo"
              label="PartagÃ© Ã "
              placeholder="Donner accÃ¨s Ã  des utilisateurs"
              options={
                users?.map((user) => ({
                  value: String(user.id),
                  label: user.fullname,
                })) || []
              }
            />
          </Card>
        </Col>
      </Row>
      <Br height={spacing.xl} />
      <Row>
        <Col span={spacing.m}>
          <Card title="PiÃ¨ces jointes" bordered={false}>
            <FieldUpload<FormAuditSchema>
              errorMessage={errors.attachedFiles?.message}
              name="attachedFiles"
              control={control}
              listType="picture"
              label=""
              placeholder=""
            />
          </Card>
        </Col>
      </Row>
      <Br height={spacing.xl} />
      <Row justify="end">
        <Button
          type="primary"
          htmlType="submit"
          className="large-padding-btn"
          loading={isLoading}
        >
          {isEditMode ? "Modifier" : "Publier"}
        </Button>
      </Row>
    </Form>
  );
};

export default FormAudit;
